import React, { useState,useEffect } from "react";
import * as Yup from "yup";
import { storeAuthToken, storeOrganizerId, storeUserDetails } from '../../utils/auth'
import { useFormik } from "formik";
import { requestOTP,verifyOTP ,createVenueAccount, userLogin, getAllCountries } from "../../api"
import { OtpPinInput } from "../../components/OtpPinInput";
import { useNavigate } from "react-router-dom";
import "./RegisterPage.css";
type Props = {}

const RegisterPage = (props: Props) => {
	const [otpSent, setOtpSent] = useState(false);
	const [OTP, setOTP] = useState("");
  	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");
    const [registrationId, setRegistrationId] = useState<string | null>(null);
	const [userEmail,setEmail] = useState<string | null>(null);
	const [otpVerified, setOtpVerified] = useState(false);
	const [userDetails, setUserDetails] = useState({ name: "" });
	const [countries, setCountries] = useState<any[]>([]);
	const [selectedCountryId, setSelectedCountryId] = useState("");
	const [countriesLoading, setCountriesLoading] = useState(false);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [passwordError, setPasswordError] = useState("");
	const [confirmPasswordError, setConfirmPasswordError] = useState("");
	const navigate = useNavigate();
	const [timer, setTimer] = useState(30);

	/* Match login/reset auth pages so dark form styles apply on register. */
	useEffect(() => {
		document.body.classList.add("ex-theme");
	}, []);

	useEffect(() => {
	let interval: NodeJS.Timeout;
	if (otpSent && timer > 0) {
		interval = setInterval(() => {
		setTimer((prev) => prev - 1);
		}, 1000);
	}
  return () => clearInterval(interval);
}, [otpSent, timer]);

	// Load countries once the profile step is shown (needed for venue countryId).
	useEffect(() => {
		if (!otpVerified) return;
		let cancelled = false;
		(async () => {
			try {
				setCountriesLoading(true);
				const res = await getAllCountries();
				if (!cancelled) setCountries(res || []);
			} catch (err) {
				console.error("Failed to load countries:", err);
				if (!cancelled) setError("Failed to load countries. Please refresh and try again.");
			} finally {
				if (!cancelled) setCountriesLoading(false);
			}
		})();
		return () => { cancelled = true; };
	}, [otpVerified]);

	 const validation = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email("Invalid email")
        .required("Email is required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        setError("");
        const response = await requestOTP({ email: values.email });
        console.log("OTP request response:", response);
		if (response.registrationId) {
          setRegistrationId(response.registrationId);
        }
		if(response.email){
			setEmail(response.email);
		}
        setOtpSent(true);
      } catch (err:any) {
        console.error("Failed to request OTP:", err);
        setError(err.response?.data?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });
  const handleResendOtp = async () => {
	try {
		setLoading(true);
		setError("");
		const response = await requestOTP({ email: validation.values.email });
		console.log("Resend OTP response:", response);
		if (response.registrationId) {
		setRegistrationId(response.registrationId);
		}
		if (response.email) {
		setEmail(response.email);
		}
		setTimer(60); 
	} catch (err: any) {
		console.error("Failed to resend OTP:", err);
		setError(err.response?.data?.message || "Something went wrong");
	} finally {
		setLoading(false);
	}
	};

	const handleOtpSubmit = async (e: any) => {
		e.preventDefault();
		try {
			setLoading(true);
			setError("");

			const res = await verifyOTP({
			email: validation.values.email, 
			otp: OTP,     
			registrationId,                  
			});
			console.log("OTP verification response:", res);
			if (res.verified === true) {
			setOtpVerified(true);
			} else {
			setError("Invalid OTP. Please try again.");
			}
		} catch (err: any) {
			console.error("OTP verification failed:", err);
			setError(err.response?.data?.message || "Something went wrong");
		} finally {
			setLoading(false);
		}
		};
		const handleDetailsSubmit = async (e: any) => {
			e.preventDefault();
			try {
				setLoading(true);
				setError("");
				setPasswordError("");
				setConfirmPasswordError("");
				const email = (userEmail || validation.values.email || "").trim().toLowerCase();
				const name = (userDetails.name || "").trim();
				if (!name) {
					setError("Please enter your name");
					return;
				}
				if (!selectedCountryId) {
					setError("Please select a country");
					return;
				}
				if (!password || password.length < 8) {
					setPasswordError("Password must be at least 8 characters");
					return;
				}
				if (password !== confirmPassword) {
					setConfirmPasswordError("Passwords must match");
					return;
				}

				// Venue accounts are role 3 (not organizer signup complete).
				const formData = new FormData();
				formData.append("username", email);
				formData.append("email", email);
				formData.append("name", name);
				formData.append("password", password);
				formData.append("userRole", "3");
				formData.append("phoneNo", "");
				formData.append("address", "");
				// Backend expects location as countryId (bigint) for venue create.
				formData.append("location", String(selectedCountryId));

				const created = await createVenueAccount(formData);
				if (!created?.userId) {
					setError("Could not create venue account. Please try again.");
					return;
				}

				const loginRes: any = await userLogin({
					username: email,
					password,
				});
				if (!loginRes?.user || !loginRes?.token) {
					setError("Account created. Please sign in with your email and password.");
					navigate("/login");
					return;
				}

				storeAuthToken(loginRes.token);
				storeUserDetails(loginRes.user);
				storeOrganizerId(Number(loginRes.user.userId));
				localStorage.setItem("venueData", JSON.stringify(loginRes.user));
				navigate("/home");
			} catch (err: any) {
				console.error("Registration failed:", err);
				setError(err.response?.data?.message || err.response?.data?.error || "Something went wrong");
			} finally {
				setLoading(false);
			}
			};

	return (
		<div className="form-wrapper">
			<div className="app-form">
				<div className="app-form-sidebar">
					<div className="sidebar-sign-logo">
						<img src="images/sign-logo.svg" alt=""/>
					</div>
					<div className="sign_sidebar_text">
						<h1>The Easiest Way to Create Events and Sell More Tickets Online</h1>
					</div>
				</div>
				<div className="app-form-content">
					<div className="container">
						<div className="row justify-content-center">
							<div className="col-lg-10 col-md-10">
								<div className="app-top-items">
									<a href="index.html">
										<div className="sign-logo" id="logo">
											<img src="images/logo.svg" alt=""/>
											<img className="logo-inverse" src="images/dark-logo.svg" alt=""/>
										</div>
									</a>
									<div className="app-top-right-link">
										Already have an account?<a className="sidebar-register-link" href="/login">Sign In</a>
									</div>
								</div>
						</div>
							<div className="col-xl-5 col-lg-6 col-md-7">
								<div className="registration">
									{!otpSent ? (
										<form onSubmit={validation.handleSubmit}>
											<h2 className="registration-title">Sign up to EventoX</h2>
											<div className="form-group mt-4">
												<label className="form-label">Your Email*</label>
												<input
													className="form-control h_50"
													type="email"
													name="email"
													placeholder="Enter email address"
													value={validation.values.email}
													onChange={validation.handleChange}
													onBlur={validation.handleBlur}
												/>
												{validation.touched.email && validation.errors.email && (
													<div className="text-danger">{validation.errors.email}</div>
												)}
											</div>
											<div className="mt-4">
												<button className="gradient-button" type="submit" disabled={loading}>
													<h5>{ loading ? "Sending OTP...":"Send OTP"} <i className="fas fa-sign-in-alt ms-2"></i></h5>
												</button>
												<p className="mt-4 text-danger">{error}</p>
				
											</div>
										</form>
									) : !otpVerified ?(
										<form onSubmit={handleOtpSubmit}>
											<h2 className="registration-title">Verify your Email</h2>
											<div className="form-group mt-4">
												<OtpPinInput
													value={OTP}
													onChange={setOTP}
													boxStyle={{
														border: "2px solid #ccc",
														background: "#fff",
														color: "#111",
													}}
												/>
											</div>
											<div className="mt-4">
												<button className="gradient-button" type="submit"
												disabled={loading}
												>
													<h5>{loading ? "Verifying OTP..." : "Verify OTP"} <i className="fas fa-check ms-2"></i></h5>
												</button>
												<p className="mt-4 text-danger">{error}</p>
											</div>
											 <div className="mt-3 text-center">
												{timer > 0 ? (
												<p className="text-muted">Re-send available in {timer}s</p>
												) : (
												<p>
													Didn’t receive code?{" "}
													<span
													className="text-primary"
													style={{ cursor: "pointer", fontWeight: "500" }}
													onClick={handleResendOtp}
													>
													Resend
													</span>
												</p>
												)}
											</div>
										</form>
									):(
										<form onSubmit={handleDetailsSubmit}>
											<h2 className="registration-title">Complete Your Profile</h2>
											<div className="form-group mt-4">
											<label className="form-label">Full Name*</label>
											<input
												className="form-control h_50"
												type="text"
												name="name"
												placeholder="Enter your full name"
												value={userDetails.name}
												onChange={(e) => setUserDetails({ ...userDetails, name: e.target.value })}
											/>
											</div>
											<div className="form-group mt-4">
												<label className="form-label">Country*</label>
												<select
													className="form-control h_50"
													name="country"
													value={selectedCountryId}
													onChange={(e) => setSelectedCountryId(e.target.value)}
													disabled={countriesLoading}
													style={{ colorScheme: "dark" }}
												>
													<option value="" style={{ backgroundColor: "#1a1a1f", color: "#fff" }}>
														{countriesLoading ? "Loading countries..." : "Select Country"}
													</option>
													{countries.map((c: any) => (
														<option
															key={c.countryId}
															value={c.countryId}
															style={{ backgroundColor: "#1a1a1f", color: "#fff" }}
														>
															{c.countryName}
														</option>
													))}
												</select>
											</div>
											<div className="form-group mt-4">
												<label className="form-label">Password*</label>
												<div className="loc-group position-relative">
													<input
														className="form-control h_50"
														type={showPassword ? "text" : "password"}
														name="password"
														placeholder="At least 8 characters"
														value={password}
														onChange={(e) => {
															setPassword(e.target.value);
															setPasswordError("");
														}}
														autoComplete="new-password"
													/>
													<span
														className="pass-show-eye"
														onClick={() => setShowPassword(!showPassword)}
														style={{ cursor: "pointer" }}
													>
														<i className={showPassword ? "fas fa-eye" : "fas fa-eye-slash"}></i>
													</span>
												</div>
												{passwordError && <div className="text-danger">{passwordError}</div>}
											</div>
											<div className="form-group mt-4">
												<label className="form-label">Confirm Password*</label>
												<div className="loc-group position-relative">
													<input
														className="form-control h_50"
														type={showConfirmPassword ? "text" : "password"}
														name="confirmPassword"
														placeholder="Re-enter your password"
														value={confirmPassword}
														onChange={(e) => {
															setConfirmPassword(e.target.value);
															setConfirmPasswordError("");
														}}
														autoComplete="new-password"
													/>
													<span
														className="pass-show-eye"
														onClick={() => setShowConfirmPassword(!showConfirmPassword)}
														style={{ cursor: "pointer" }}
													>
														<i className={showConfirmPassword ? "fas fa-eye" : "fas fa-eye-slash"}></i>
													</span>
												</div>
												{confirmPasswordError && (
													<div className="text-danger">{confirmPasswordError}</div>
												)}
											</div>
											<div className="mt-4">
											<button className="gradient-button" type="submit" disabled={loading || countriesLoading}>
												<h5>{loading ? "Submitting..." : "Finish Signup"} <i className="fas fa-user-check ms-2"></i></h5>
											</button>
											<p className="mt-4 text-danger">{error}</p>
											</div>
										</form>
									)}
									<div className="agree-text">
										By clicking "Sign up", you agree to EventoX{" "}
										<a href="https://eventox.co/about-us/">Terms & Conditions</a> and have read the{" "}
										<a href="https://eventox.co/about-us/">Privacy Policy</a>.
									</div>
									<div className="new-sign-link">
										Already have an account?
										<a className="signup-link" href="/login">Sign In</a>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="copyright-footer">
						© {new Date().getFullYear()}, EventoX. All rights reserved.
					</div>
				</div>
			</div>
		</div>
	);
};

export default RegisterPage;
